import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { ConfirmModal } from '@/components/ConfirmModal';
import { KstDataGrid } from '@/components/KstDataGrid';
import { Dropdownlist } from '@/components/Dropdownlist';
import { assignToStopsColumns } from '../tour-management-config';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';

interface AssignToStopsModalProps {
  open: boolean;
  tasks: any[]; // The tasks to assign (each has .id and .name)
  onClose: () => void;
  onComplete: () => void; // Called after assignment completes (to refresh parent grid)
}

export function AssignToStopsModal({ open, tasks, onClose, onComplete }: AssignToStopsModalProps) {
  const [tourStopRows, setTourStopRows] = useState<{ entries: any[] | null }>({ entries: null });
  const [loading, setLoading] = useState(false);
  const [tours, setTours] = useState<any[]>([]);
  const [selectedTourId, setSelectedTourId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItems, setConfirmItems] = useState<any[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [tourStopIdsForSelectedTour, setTourStopIdsForSelectedTour] = useState<Set<number> | null>(null);

  // Keep a ref to the full entries array so we can read isChecked at action time
  const allEntriesRef = useRef<any[]>([]);

  // Load all tour stops
  const loadTourStops = useCallback(() => {
    setLoading(true);
    api.getTourStops(1, '10000', `{"data":{"take":10000,"skip":0,"page":1,"pageSize":10000}}`, (result: any) => {
      const entries = (result.entries || []).map((row: any) => ({ ...row, isChecked: false }));
      entries.sort((a: any, b: any) => (a.title > b.title) ? 1 : -1);
      allEntriesRef.current = entries;
      setTourStopRows({ entries });
      setLoading(false);
    });
  }, []);

  // Load tours for the filter dropdown
  const loadTours = useCallback(() => {
    api.getTours(1, '10000', `{"data":{"take":10000,"skip":0,"page":1,"pageSize":10000}}`, (result: any) => {
      setTours(result.rows || result.entries || []);
    });
  }, []);

  useEffect(() => {
    if (open) {
      loadTourStops();
      loadTours();
      setSelectedTourId(null);
      setTourStopIdsForSelectedTour(null);
      setConfirmOpen(false);
      setConfirmItems([]);
    }
  }, [open, loadTourStops, loadTours]);

  // When a tour is selected, load its stops to get the IDs for filtering
  useEffect(() => {
    if (selectedTourId == null) {
      setTourStopIdsForSelectedTour(null);
      return;
    }
    api.getTourStopsForTour(selectedTourId, (result: any) => {
      const ids = new Set<number>((result.entries || []).map((e: any) => e.id));
      setTourStopIdsForSelectedTour(ids);
    });
  }, [selectedTourId]);

  // Apply tour filter to the rows shown in the grid
  const filteredRows = useMemo(() => {
    if (!tourStopRows.entries) return { entries: null };
    if (!tourStopIdsForSelectedTour) return tourStopRows;
    const filtered = tourStopRows.entries.filter((row: any) => tourStopIdsForSelectedTour.has(row.id));
    return { entries: filtered };
  }, [tourStopRows, tourStopIdsForSelectedTour]);

  const taskNames = tasks.map(t => t.name).join(', ');

  const handleTourFilterChange = useCallback((value: string | number | null) => {
    setSelectedTourId(value as number | null);
  }, []);

  const tourDropdownItems = useMemo(() => {
    return tours;
  }, [tours]);

  // Read checked items from the row objects at action time (grid mutates isChecked directly)
  const handleAssignClick = useCallback(() => {
    const checked = allEntriesRef.current.filter((r: any) => r.isChecked === true);
    if (checked.length === 0) {
      App.showPopupNotification('Please select at least one tour stop.', 'warning');
      return;
    }
    setConfirmItems(checked);
    setConfirmOpen(true);
  }, []);

  const handleConfirmAssign = useCallback(() => {
    setAssigning(true);
    const taskIds = tasks.map(t => t.id);
    const tourStopIds = confirmItems.map((item: any) => item.id);
    api.assignTasksToStops(taskIds, tourStopIds, (result: any) => {
      setAssigning(false);
      setConfirmOpen(false);
      const msg = `${result.assignedCount} task(s) assigned to tour stop(s).${result.skippedDuplicates > 0 ? ` ${result.skippedDuplicates} duplicate(s) skipped.` : ''}`;
      App.showPopupNotification(msg, 'success');
      onComplete();
      onClose();
    });
  }, [tasks, confirmItems, onComplete, onClose]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  return (
    <>
      <BaseFormModal
        open={open}
        title="Assign Task(s) to Tour Stops"
        dialogClass="kstc-dialog--assign-stops"
        saveText="Assign"
        cancelText="Cancel"
        onSave={handleAssignClick}
        onCancel={onClose}
      >
        <p style={{ marginBottom: '0.5rem' }}><strong>Tasks:</strong> {taskNames}</p>

        <div style={{ marginBottom: '0.75rem' }}>
          <label className="kstc-label" style={{ marginBottom: '0.25rem', display: 'block' }}>Filter by tour:</label>
          <Dropdownlist
            value={selectedTourId}
            items={tourDropdownItems}
            labelKey="title"
            valueKey="id"
            placeholder="All Tours"
            clearable={true}
            onChange={handleTourFilterChange}
          />
        </div>

        <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
          <KstDataGrid
            columns={assignToStopsColumns}
            rows={filteredRows}
            loading={loading}
            showcheckbox={true}
            showheader={false}
            mobiletitlefield="title"
          />
        </div>
      </BaseFormModal>

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Assignment"
        confirmText="Confirm"
        confirmClass="primary"
        busy={assigning}
        onConfirm={handleConfirmAssign}
        onCancel={handleCancelConfirm}
      >
        <p>Assign {tasks.length} task(s) to {confirmItems.length} tour stop(s)?</p>
        <div style={{ marginTop: '0.5rem' }}>
          <strong>Tasks:</strong>
          <ul style={{ margin: '0.25rem 0 0.5rem 1.25rem' }}>
            {tasks.map(t => <li key={t.id}>{t.name}</li>)}
          </ul>
          <strong>Tour Stops:</strong>
          <ul style={{ margin: '0.25rem 0 0 1.25rem' }}>
            {confirmItems.map((item: any) => <li key={item.id}>{item.title}</li>)}
          </ul>
        </div>
      </ConfirmModal>
    </>
  );
}
